export interface UseSqPaymentForm {
  (props: UseSqPaymentFormProps): UseSqPaymentFormReturn
}

export type UseSqPaymentFormProps = {
  config: UseSqPaymentFormConfig
  options?: UseSqPaymentFormOptions
  callbacks?: SqPaymentFormCallbacks
}

export type UseSqPaymentFormReturn = {
  instance: SqPaymentFormInstance
  state: states
  response: UseSqPaymentFormResponse
  actions: {
    getNonce: UseSqPaymentFormGetNonce
  }
}

export type UseSqPaymentFormGetNonce = (
  verificationDetails: SqVerificationDetails
) => void

export type UseSqPaymentFormConfig = {
  applePay?: InputTarget
  applicationId: string
  autoBuild?: boolean
  callbacks?: SqPaymentFormCallbacks
  card?: InputTarget
  cardNumber?: InputTarget
  cvv?: InputTarget
  expirationDate?: InputTarget
  giftCard?: InputTarget
  googlePay?: InputTarget
  locationId?: string
  masterpass?: InputTarget
  postalCode?: InputTarget
  inputClass?: string
  inputStyles?: InputStyle[]
}

export type UseSqPaymentFormOptions = {
  verifyBuyer: boolean
}

export type UseSqPaymentFormResponse = {
  errors: {
    nonce: SqResponseError[]
    verification: SqVerificationError[]
  }
  nonce: string
  cardData: SqCardData
  billingContact: SqContact
  shippingContact: SqContact
  shippingOption: SqShippingOption
  verificationResult: SqVerificationResult
}

export enum states {
  INITIALIZING = 'INITIALIZING',
  LOADING_API = 'LOADING_API',
  API_LOADED = 'API_LOADED',
  BUILDING_FORM = 'BUILDING_FORM',
  IDLE = 'IDLE',
  REQUESTING_NONCE = 'REQUESTING_NONCE',
  NONCE_RECEIVED = 'NONCE_RECEIVED',
  VERIFYING_BUYER = 'VERIFYING_BUYER',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  HTTPS_REQUIRED = 'HTTPS_REQUIRED',
  SYSTEM_ERROR = 'SYSTEM_ERROR',
}

// SqPaymentForm Data Types
// https://developer.squareup.com/docs/api/paymentform#sqpaymentrequestdatatypes

export type SqPaymentFormInstance = {
  build: () => void
  destroy: () => void
  done: (paymentDetailsUpdate: object) => boolean
  isSupportedBrowser: () => boolean
  masterpassImageUrl: (assetUrl: string) => void
  recalculateSize: () => void
  setPostalCode: (postalCod: string) => void
  focus: (field: 'cardNumber' | 'cvv' | 'expirationDate' | 'postalCode') => void
  requestCardNonce: () => void
  verifyBuyer: () => void
}

export type SqPaymentFormCallbacks = {
  cardNonceResponseReceived?: (
    instance: SqPaymentFormInstance,
    errors: SqResponseError[],
    nonce: string,
    cardData: SqCardData,
    billingContact: SqContact,
    shippingContact: SqContact,
    shippingOption: SqShippingOption
  ) => void
  createPaymentRequest?: () => void
  inputEventReceived?: (
    instance: SqPaymentFormInstance,
    inputEvent: SqInputEventData
  ) => void
  methodsSupported?: () => void
  paymentFormLoaded?: () => void
  unsupportedBrowserDetected?: () => void
  shippingContactChanged?: () => void
  shippingOptionChanged?: () => void
  verifyBuyerResponseReceived?: (
    errors: SqVerificationError[],
    verificationResult: SqVerificationResult
  ) => void
  paymentFormResponseComplete?: (
    response: UseSqPaymentFormResponse,
    billingContact: SqContact
  ) => void
}

export type SqInputEventData = {
  cardBrand: string
  currentState: SqInputEventDataState
  elementId: string
  eventType: string
  field: string
  postalCodeValue: string
  previousState: SqInputEventDataState
}

export type SqInputEventDataState = {
  hasErrorClass: boolean
  hasFocusClass: boolean
  isCompletelyValid: boolean
  isEmpty: boolean
  isPotentiallyValid: boolean
}

export type SqResponseError = {
  type: string
  message: string
  field: string
}

export type SqContact = {
  familyName: string
  givenName: string
  email: string
  country: string
  countryName: string
  region: string
  city: string
  addressLines: string[]
  postalCode: string
  phone: string
}

export type SqShippingOption = {
  id: string
  label: string
  amount: string
}

export type SqVerificationDetails = {
  billingContact: SqContact
  amount: string
  currencyCode: string
  intent: 'CHARGE' | 'STORE'
}

export type SqVerificationError = {
  type: string
  message: string
}

export type SqVerificationResult = {
  token: string
  userChallenged: boolean
}

export type SqCardData = {
  card_brand: CardBrands
  last_4: string
  exp_month: string
  exp_year: string
  billing_postal_code: string
  digital_wallet_type: DigitalWalletTypes
}

export type InputTarget = {
  elementId: string
  placeholder?: string
  inputStyle?: InputStyle
}

export type InputStyle = {
  mediaMinWidth?: string
  mediaMaxWidth?: string
  backgroundColor?: string
  borderRadius?: string
  boxShadow?: string
  color?: string
  details?: InputStyleDetails
  error?: InputStyleError
  fontFamily?: SqFontFamily
  fontSize?: string
  fontWeight?: string
  letterSpacing?: string
  lineHeight?: string
  padding?: string
  placeholderColor?: string
  placeholderFontWeight?: string
}

export type InputStyleDetails = {
  hidden?: boolean
  color?: string
  fontFmaily?: SqFontFamily
  fontSize?: string
  fontWeight?: string
  error?: InputStyleError
}

export type InputStyleError = {
  backgroundColor?: string
  boxShadow?: string
  cardIconColor?: string
  color?: string
  fontFamily?: SqFontFamily
  fontSize?: string
  fontWeight?: string
  outlineColor?: string
}

export type CardBrands =
  | 'americanExpress'
  | 'discover'
  | 'discoverDiners'
  | 'JCB'
  | 'masterCard'
  | 'unionPay'
  | 'unknown'
  | 'visa'

export type DigitalWalletTypes =
  | 'APPLE_PAY'
  | 'GOOGLE_PAY'
  | 'MASTERPASS'
  | 'NONE'

export type SqFontFamily =
  | 'andale mono'
  | 'arial'
  | 'arial black'
  | 'arial narrow'
  | 'arial rounded mt bold'
  | 'avant garde'
  | 'baskerville'
  | 'big caslon'
  | 'bodoni mt'
  | 'book antiqua'
  | 'brush script mt'
  | 'calibri'
  | 'calisto mt'
  | 'cambria'
  | 'candara'
  | 'century gothic'
  | 'charcoal'
  | 'comic sans ms'
  | 'consolas'
  | 'copperplate'
  | 'copperplate gothic light'
  | 'courier'
  | 'courier new'
  | 'cursive'
  | 'didot'
  | 'fantasy'
  | 'franklin gothic medium'
  | 'futura'
  | 'garamond'
  | 'geneva'
  | 'georgia'
  | 'gill sans'
  | 'goudy old style'
  | 'helvetica'
  | 'helvetica neue'
  | 'hoefler text'
  | 'impact'
  | 'lucida bright'
  | 'lucida console'
  | 'lucida grande'
  | 'lucida sans unicode'
  | 'lucida sans typewriter'
  | 'monaco'
  | 'monospace'
  | 'optima'
  | 'palatino'
  | 'palatino linotype'
  | 'papyrus'
  | 'perpetua'
  | 'rockwell'
  | 'rockwell extra bold'
  | 'sans-serif'
  | 'segoe ui'
  | 'serif'
  | 'tahoma'
  | 'times'
  | 'times new roman'
  | 'trebuchet ms'
  | 'verdana'
